<!-- ── Header (sticky) ─────────────────────────────────────────────── -->
<div class="quick-capture__header">
  <div class="quick-capture__title-row">
    <h2 id="quick-capture-title" class="quick-capture__title">
      <span class="quick-capture__title-icon">
        <i class="bi bi-lightning-charge-fill" aria-hidden="true"></i>
      </span>
      <span class="quick-capture__title-text">{{ 'QuickCapture.title' | translate }}</span>
    </h2>
    <button type="button"
            class="btn btn-link text-muted p-1"
            (click)="cancel()"
            [attr.aria-label]="'General.close' | translate">
      <i class="bi bi-x-lg" aria-hidden="true"></i>
    </button>
  </div>
</div>

<!-- ── Body ────────────────────────────────────────────────────────── -->
<div class="quick-capture__body">

  <div *ngIf="loading" class="text-center py-5">
    <div class="spinner-border text-secondary" role="status">
      <span class="visually-hidden">{{ 'General.loading' | translate }}</span>
    </div>
  </div>

  <form *ngIf="!loading"
        [formGroup]="form"
        (ngSubmit)="save()"
        class="quick-capture__form quick-capture__block">

    <div class="quick-capture__block-header">
      <i class="bi bi-card-checklist" aria-hidden="true"></i>
      <span>{{ 'QuickCapture.blockHeader' | translate }}</span>
    </div>

    <!-- Title — prominent inline-headline style -->
    <div>
      <label class="form-label" for="qc-title">
        {{ 'QuickCapture.taskTitleLabel' | translate }} <span class="text-danger">*</span>
      </label>
      <input id="qc-title" #titleInput type="text"
             class="form-control form-control-sm quick-capture__title-input"
             formControlName="taskTitle"
             [placeholder]="'QuickCapture.taskTitlePlaceholder' | translate"
             maxlength="500" />
      <div *ngIf="form.get('taskTitle')?.invalid && form.get('taskTitle')?.touched"
           class="invalid-feedback d-block small">
        {{ 'QuickCapture.errorNoTitle' | translate }}
      </div>
    </div>

    <!-- Assignee — full width (long names need room) -->
    <div>
      <label class="form-label" for="qc-assignee">
        {{ 'QuickCapture.assignee' | translate }} <span class="text-danger">*</span>
      </label>
      <select id="qc-assignee" class="form-select form-select-sm" formControlName="assignedToUserId">
        <option *ngFor="let u of users" [ngValue]="u.userId">{{ u.userFullname }}</option>
      </select>
    </div>

    <!-- Project — full width with rich ng-select item template -->
    <div>
      <label class="form-label" for="qc-project">{{ 'QuickCapture.project' | translate }}</label>
      <ng-select id="qc-project"
                 formControlName="projectId"
                 [items]="projectsForPicker"
                 groupBy="groupLabel"
                 [selectableGroup]="false"
                 bindLabel="projectTitle"
                 bindValue="projectId"
                 [placeholder]="'QuickCapture.noProject' | translate"
                 [clearable]="true"
                 [searchable]="true"
                 [virtualScroll]="true"
                 appendTo="body"
                 class="custom-sm">
        <ng-template ng-optgroup-tmp let-item="item">
          <span class="text-uppercase small fw-semibold text-muted">{{ item.groupLabel }}</span>
        </ng-template>
        <ng-template ng-option-tmp let-item="item">
          <div class="d-flex flex-column">
            <span>{{ item.projectTitle }}</span>
            <small *ngIf="item.customerName || item.projNo" class="text-muted">
              <span *ngIf="item.projNo">{{ item.projNo }}</span>
              <span *ngIf="item.projNo && item.customerName"> · </span>
              <span *ngIf="item.customerName">{{ item.customerName }}</span>
            </small>
          </div>
        </ng-template>
      </ng-select>
    </div>

    <!-- Due date + Priority — side-by-side -->
    <div class="quick-capture__field-row">
      <div>
        <label class="form-label" for="qc-due">{{ 'QuickCapture.dueDate' | translate }}</label>
        <input id="qc-due" type="date" class="form-control form-control-sm"
               formControlName="taskDueDate" />
      </div>
      <div>
        <label class="form-label" for="qc-priority">{{ 'QuickCapture.priority' | translate }}</label>
        <select id="qc-priority" class="form-select form-select-sm" formControlName="taskPriority">
          <option value="Low">{{ 'QuickCapture.priorityLow' | translate }}</option>
          <option value="Medium">{{ 'QuickCapture.priorityMedium' | translate }}</option>
          <option value="High">{{ 'QuickCapture.priorityHigh' | translate }}</option>
        </select>
      </div>
    </div>

    <!-- Description — always visible, full width -->
    <div>
      <label class="form-label" for="qc-description">{{ 'QuickCapture.description' | translate }}</label>
      <textarea id="qc-description" class="form-control form-control-sm" rows="4"
                formControlName="taskDescription"
                [placeholder]="'QuickCapture.descriptionPlaceholder' | translate"
                maxlength="4000"></textarea>
    </div>

  </form>
</div>

<!-- ── Footer (shortcut hint + actions) ────────────────────────────── -->
<div class="quick-capture__footer">
  <span class="quick-capture__shortcut-hint">
    <kbd>Ctrl</kbd> + <kbd>Enter</kbd>
    {{ 'QuickCapture.toSave' | translate }}
  </span>
  <div class="quick-capture__footer-actions">
    <button type="button" class="btn btn-sm btn-outline-secondary" (click)="cancel()" [disabled]="saving">
      {{ 'General.cancel' | translate }}
    </button>
    <button type="button" class="btn btn-sm btn-action-dark" (click)="save()"
            [disabled]="saving || form.invalid">
      <span *ngIf="saving" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
      <i class="bi bi-check2 me-1" *ngIf="!saving" aria-hidden="true"></i>
      {{ 'QuickCapture.save' | translate }}
    </button>
  </div>
</div>
